import FileSaver from 'file-saver'
import { REGIONS } from '../../utils/constants'
import { monthYearDate } from '../../utils/format/date'
import { sortDates, sortObjectsArray } from '../../utils/functions/common'
import { setBackGroundColor, setBold, setCellNumberFormat } from './common'
import {
  addTableHeader,
  addTableRow,
  addTableTotalRow,
  allColumns,
  defaultColumnsCount,
  getColumnLayout,
  setColumnsWidth,
} from './commonStatementFunctions'
const Excel = require('exceljs')

const totalColor = '95B3D7'

const _getCoopGroups = (amounts) =>
  sortObjectsArray([...new Map(amounts.map((amount) => [amount.coopGroup.id, amount.coopGroup])).values()], 'name')

const _getColumnsFromAmounts = (amounts) => {
  if (!amounts || amounts.length === 0) return []

  const columnsMap = new Map()
  for (const amount of amounts) {
    if (!amount.coopGroup) continue
    const columnValues = amount.coopGroup.columnValues || []
    for (const columnValue of columnValues) {
      if (columnValue.column && !columnsMap.has(columnValue.column.id)) {
        columnsMap.set(columnValue.column.id, columnValue.column)
      }
    }
  }
  return [...columnsMap.values()].sort((a, b) => a.position - b.position)
}

function _createSupplierStatementsReport(worksheet, statement, row, rowWithValues, columns, layout) {
  const startingRow = row
  const coopGroups = _getCoopGroups(statement.amounts)

  const regionsStartCol = allColumns[layout.regionsStart]

  const headerCellsValues = {
    A: monthYearDate(statement.period),
    [regionsStartCol]: 'CA et volume déclarés',
    [allColumns[layout.accordsRepeatIdx]]: monthYearDate(statement.period),
  }

  columns.forEach((col, idx) => {
    const block = layout.rfdBlocks[idx]
    if (block) {
      headerCellsValues[allColumns[block.start]] = `Montant ${col.name}`
    }
  })

  if (columns.length === 0) {
    headerCellsValues[allColumns[layout.rfdBlocks[0]?.start]] = 'Montant Coop Filiales'
    headerCellsValues[allColumns[layout.rfdBlocks[1]?.start]] = 'Montant Coop SAS'
  }

  headerCellsValues[allColumns[layout.finalBlockStart]] = 'Montant Coop Total'

  const mergePairs = [[regionsStartCol, allColumns[layout.totalGeneralIdx]]]
  layout.rfdBlocks.forEach((block) => {
    mergePairs.push([allColumns[block.start], allColumns[block.totalIdx]])
  })
  mergePairs.push([allColumns[layout.finalBlockStart], allColumns[layout.finalTotalIdx]])

  worksheet.addRow(layout.cells.map((cell) => headerCellsValues[cell] ?? ''))
  setBold(worksheet, row, true)
  worksheet.getRow(row).alignment = { vertical: 'middle', horizontal: 'center' }
  mergePairs.forEach(([start, end]) => {
    worksheet.mergeCells(`${start}${row}:${end}${row}`)
  })

  addTableHeader(worksheet, row + 1, columns)

  row = row + 2
  rowWithValues.push(row)
  for (const coopGroup of coopGroups) {
    const filteredAmounts = statement.amounts.filter((amount) => amount.coopGroup.id === coopGroup.id)
    const amounts = REGIONS.map((region) => filteredAmounts.find((amount) => amount.region === region.id)?.amount ?? 0)
    addTableRow(worksheet, coopGroup, amounts, row, columns)
    row++
  }

  addTableTotalRow(worksheet, startingRow, row, columns)
  worksheet.addRow()

  return row + 2
}

function _createTotalTable(worksheet, row, rowWithValues, statementAmounts, columns, layout) {
  const startingRow = row
  const coopGroups = _getCoopGroups(statementAmounts)

  worksheet.addRow(['Total'])
  setBold(worksheet, row, true)
  worksheet.getRow(row).alignment = { vertical: 'middle' }
  setBackGroundColor(worksheet, `A${row}`, totalColor)
  worksheet.mergeCells(`A${row}:${allColumns[layout.finalTotalIdx]}${row}`)
  row++

  addTableHeader(worksheet, row, columns)

  const regionsStartIdx = layout.regionsStart
  const cellsAmounts = []
  for (let i = regionsStartIdx; i <= layout.regionsEnd; i++) {
    cellsAmounts.push(allColumns[i])
  }

  const amountsData = []
  for (const [index, coopGroup] of coopGroups.entries()) {
    row++
    const cellsWithFormula = cellsAmounts.map((cell) => ({
      formula: rowWithValues
        .map((rowNumber) => `${cell}${rowNumber + index}`)
        .toString()
        .replaceAll(',', '+'),
    }))
    addTableRow(worksheet, coopGroup, cellsWithFormula, row, columns)

    amountsData.push({
      coopGroup: coopGroup.name,
      cellNumber: row,
    })
  }
  addTableTotalRow(worksheet, startingRow, row + 1, columns)
  worksheet.addRow()

  return { newRowValue: row + 3, amountsData }
}

function _setTotalSupplier(
  worksheet,
  row,
  rowWithValues,
  statementAmounts,
  supplierName,
  totalAmountsCellsBySuppliers,
  columns,
  layout
) {
  const supplierCells = _getSupplierAmountsCells(layout)
  const cellsMapping = _getSupplierColumnsCellsMapping(layout, columns)

  const { newRowValue, amountsData } = _createTotalTable(
    worksheet,
    row,
    rowWithValues,
    statementAmounts,
    columns,
    layout
  )

  const existingTotalIndex = totalAmountsCellsBySuppliers.findIndex((total) => total.supplier === supplierName)

  if (existingTotalIndex === -1) {
    totalAmountsCellsBySuppliers.push({
      supplier: supplierName,
      amounts: amountsData,
      columns,
      supplierAmountsCells: supplierCells,
      cellsMapping,
    })
  } else {
    totalAmountsCellsBySuppliers[existingTotalIndex] = {
      ...totalAmountsCellsBySuppliers[existingTotalIndex],
      amounts: [...totalAmountsCellsBySuppliers[existingTotalIndex].amounts, ...amountsData],
    }
  }

  return newRowValue
}

function _getUniqueColumns(totalAmountsCellsBySuppliers) {
  const allSupplierColumns = totalAmountsCellsBySuppliers.flatMap((t) => t.columns || [])
  const uniqueColumnsMap = new Map()
  for (const col of allSupplierColumns) {
    if (col && !uniqueColumnsMap.has(col.id)) {
      uniqueColumnsMap.set(col.id, col)
    }
  }
  return [...uniqueColumnsMap.values()].sort((a, b) => a.position - b.position)
}

function _buildTotalTabCellsLayout(columnsCount) {
  const regionsCount = REGIONS.length

  const totalTurnoverCells = []
  for (let i = 0; i <= regionsCount; i++) {
    totalTurnoverCells.push(allColumns[1 + i])
  }

  const columnBlocks = []
  let currentIdx = totalTurnoverCells.length + 1
  for (let i = 0; i < columnsCount; i++) {
    const block = []
    for (let j = 0; j <= regionsCount; j++) {
      block.push(allColumns[currentIdx + j])
    }
    columnBlocks.push(block)
    currentIdx += regionsCount + 1
  }

  const totalCoopCells = []
  for (let j = 0; j <= regionsCount; j++) {
    totalCoopCells.push(allColumns[currentIdx + j])
  }

  const amountCells = [...totalTurnoverCells, ...columnBlocks.flat(), ...totalCoopCells]
  const allCells = ['A', ...amountCells]

  return { totalTurnoverCells, columnBlocks, totalCoopCells, amountCells, allCells }
}

function _getSupplierAmountsCells(layout) {
  const supplierAmountsCells = []
  for (let i = layout.regionsStart; i <= layout.regionsEnd; i++) {
    supplierAmountsCells.push(allColumns[i])
  }
  supplierAmountsCells.push(allColumns[layout.totalGeneralIdx])
  layout.rfdBlocks.forEach((block) => {
    for (let i = block.start; i <= block.totalIdx; i++) {
      supplierAmountsCells.push(allColumns[i])
    }
  })
  for (let i = layout.finalBlockStart; i <= layout.finalTotalIdx; i++) {
    supplierAmountsCells.push(allColumns[i])
  }
  return supplierAmountsCells
}

function _getSupplierColumnsCellsMapping(layout, columns) {
  const regionsCount = REGIONS.length

  const turnoverCells = []
  for (let i = layout.regionsStart; i <= layout.regionsEnd; i++) {
    turnoverCells.push(allColumns[i])
  }
  turnoverCells.push(allColumns[layout.totalGeneralIdx])

  const columnBlocksMapping = {}
  columns.forEach((column, index) => {
    const block = layout.rfdBlocks[index]
    if (block) {
      const blockCells = []
      for (let i = block.start; i <= block.totalIdx; i++) {
        blockCells.push(allColumns[i])
      }
      columnBlocksMapping[column.id] = blockCells
    }
  })

  const finalBlockCells = []
  for (let i = layout.finalBlockStart; i <= layout.finalTotalIdx; i++) {
    finalBlockCells.push(allColumns[i])
  }

  return { turnoverCells, columnBlocksMapping, finalBlockCells, regionsCount }
}

function _addTotalTabHeader(worksheet, columns, cellsLayout) {
  const HEADER_ROW = 2
  const REGIONS_ROW = 3

  const { totalTurnoverCells, columnBlocks, totalCoopCells, amountCells, allCells } = cellsLayout

  worksheet.addRow([])

  const headerCellsValues = {
    [totalTurnoverCells[0]]: 'Total CA et volume déclarés',
  }
  columns.forEach((col, idx) => {
    headerCellsValues[columnBlocks[idx][0]] = `Total ${col.name}`
  })
  if (columns.length === 0) {
    headerCellsValues[columnBlocks[0]?.[0]] = 'Total Coop Filiales'
    headerCellsValues[columnBlocks[1]?.[0]] = 'Total Coop SAS'
  }
  headerCellsValues[totalCoopCells[0]] = 'Total Coop Total'

  worksheet.addRow(allCells.map((cell) => headerCellsValues[cell] ?? ''))
  setBold(worksheet, HEADER_ROW, true)
  worksheet.getRow(HEADER_ROW).alignment = { vertical: 'middle', horizontal: 'center' }

  worksheet.mergeCells(
    `${totalTurnoverCells[0]}${HEADER_ROW}:${totalTurnoverCells[totalTurnoverCells.length - 1]}${HEADER_ROW}`
  )
  columnBlocks.forEach((block) => {
    worksheet.mergeCells(`${block[0]}${HEADER_ROW}:${block[block.length - 1]}${HEADER_ROW}`)
  })
  worksheet.mergeCells(`${totalCoopCells[0]}${HEADER_ROW}:${totalCoopCells[totalCoopCells.length - 1]}${HEADER_ROW}`)

  worksheet.columns = [{ width: 30 }, ...amountCells.map(() => ({ width: 15 }))]
  const regionCodes = REGIONS.map((region) => region.code)
  const headerRow3 = ['', ...regionCodes, 'Total CA déclaré']
  columns.forEach((col) => {
    headerRow3.push(...regionCodes, `Total ${col.name}`)
  })
  if (columns.length === 0) {
    headerRow3.push(...regionCodes, 'Total filiales')
    headerRow3.push(...regionCodes, 'Total SAS')
  }
  headerRow3.push(...regionCodes, 'Total COOP')

  worksheet.addRow(headerRow3)

  totalTurnoverCells.forEach((cell) => setBackGroundColor(worksheet, `${cell}${REGIONS_ROW}`, 'FFFF00'))
  const blockColors = ['92D050', 'EA9B56', '4BACC6', 'FF9999', '99CCFF']
  columnBlocks.forEach((block, idx) => {
    block.forEach((cell) =>
      setBackGroundColor(worksheet, `${cell}${REGIONS_ROW}`, blockColors[idx % blockColors.length])
    )
  })
  totalCoopCells.forEach((cell) => setBackGroundColor(worksheet, `${cell}${REGIONS_ROW}`, '4BACC6'))
  setBold(worksheet, REGIONS_ROW)
}

function _reduceAmountsByCoopGroup(amounts) {
  const reducedAmountsByCoopGroup = []
  for (const amount of amounts) {
    const existingIndex = reducedAmountsByCoopGroup.findIndex(
      (reducedAmount) => reducedAmount.coopGroup === amount.coopGroup
    )
    if (existingIndex !== -1) {
      reducedAmountsByCoopGroup[existingIndex].cellsNumber.push(amount.cellNumber)
    } else {
      reducedAmountsByCoopGroup.push({ coopGroup: amount.coopGroup, cellsNumber: [amount.cellNumber] })
    }
  }
  return reducedAmountsByCoopGroup
}

function _buildCellFormula(cellLetter, cellNumbers, supplierName) {
  if (!cellLetter) return ''
  return cellNumbers.map((n) => `'${supplierName}'!${cellLetter}${n}`).join('+')
}

function _buildSupplierCellsForTotalTab(total, uniqueColumns, regionsCount) {
  const { cellsMapping } = total
  if (!cellsMapping) return total.supplierAmountsCells

  const supplierCells = [...cellsMapping.turnoverCells]

  for (const uniqueColumn of uniqueColumns) {
    const columnCells = cellsMapping.columnBlocksMapping[uniqueColumn.id]
    if (columnCells) {
      supplierCells.push(...columnCells)
    } else {
      supplierCells.push(...Array(regionsCount + 1).fill(null))
    }
  }

  supplierCells.push(...cellsMapping.finalBlockCells)
  return supplierCells
}

function _addTotalTabSupplierRows(worksheet, totalAmountsCellsBySuppliers, supplierAmountsCells, cellsLayout, columns) {
  const { amountCells, allCells } = cellsLayout
  const regionsCount = REGIONS.length
  let suppliersTotalRowsFormula = ''
  let rowNumber = 3

  for (const total of totalAmountsCellsBySuppliers) {
    const reducedAmountsByCoopGroup = _reduceAmountsByCoopGroup(total.amounts)

    rowNumber++
    worksheet.addRow([
      total.supplier,
      ...amountCells.map((cell) => ({
        formula: `SUM(${cell}${rowNumber + 1}:${cell}${reducedAmountsByCoopGroup.length + rowNumber})`,
      })),
    ])
    setBold(worksheet, rowNumber)
    allCells.forEach((cell) => setCellNumberFormat(worksheet, cell, rowNumber))

    suppliersTotalRowsFormula = suppliersTotalRowsFormula
      ? `${suppliersTotalRowsFormula}+_${rowNumber}`
      : `_${rowNumber}`

    const cellsForSupplier = _buildSupplierCellsForTotalTab(total, columns, regionsCount)

    for (const reducedTotalAmount of reducedAmountsByCoopGroup) {
      const cellsValue = cellsForSupplier.map((cellLetter) =>
        _buildCellFormula(cellLetter, reducedTotalAmount.cellsNumber, total.supplier)
      )

      rowNumber++
      worksheet.addRow([
        reducedTotalAmount.coopGroup,
        ...cellsValue.map((cellValue) => (cellValue ? { formula: cellValue } : '')),
      ])
      allCells.forEach((cell) => setCellNumberFormat(worksheet, cell, rowNumber))
    }
  }

  return { rowNumber, suppliersTotalRowsFormula }
}

function _addTotalTabGrandTotal(worksheet, rowNumber, suppliersTotalRowsFormula, cellsLayout) {
  const { amountCells, allCells } = cellsLayout
  worksheet.addRow([
    'Total général',
    ...amountCells.map((cell) => ({ formula: suppliersTotalRowsFormula.replaceAll('_', cell) })),
  ])
  allCells.forEach((cell) => {
    setBackGroundColor(worksheet, `${cell}${rowNumber}`, totalColor)
    setCellNumberFormat(worksheet, cell, rowNumber)
  })
  setBold(worksheet, rowNumber)
}

function _addTotalTab(workbook, totalAmountsCellsBySuppliers) {
  const columns = _getUniqueColumns(totalAmountsCellsBySuppliers)
  const columnsCount = columns.length
  const cellsLayout = _buildTotalTabCellsLayout(columnsCount)
  const layout = getColumnLayout(columnsCount)
  const supplierAmountsCells = _getSupplierAmountsCells(layout)

  const worksheet = workbook.addWorksheet('Total', { views: [{ showGridLines: false }] })

  _addTotalTabHeader(worksheet, columns, cellsLayout)

  const { rowNumber, suppliersTotalRowsFormula } = _addTotalTabSupplierRows(
    worksheet,
    totalAmountsCellsBySuppliers,
    supplierAmountsCells,
    cellsLayout,
    columns
  )

  _addTotalTabGrandTotal(worksheet, rowNumber + 1, suppliersTotalRowsFormula, cellsLayout)
}

export default async function (periods, suppliers) {
  const workbook = new Excel.Workbook()
  const sortedSuppliers = sortObjectsArray([...suppliers], 'name')
  const sortedPeriods = sortDates([...periods])

  const totalAmountsCellsBySuppliers = []
  for (const supplier of sortedSuppliers) {
    if (!supplier.statements || supplier.statements.length === 0) continue

    const statementsByPeriods = sortedPeriods
      .map((period) => supplier.statements.find((statement) => statement.period === period))
      .filter((statement) => statement && statement.amounts && statement.amounts.length > 0)

    if (statementsByPeriods.length === 0) continue

    const supplierName = supplier.name.replace(/[:&().]/g, '').slice(0, 30)

    const worksheet = workbook.addWorksheet(supplierName, {
      views: [{ showGridLines: false }],
    })

    const allAmounts = statementsByPeriods.flatMap((statement) => statement.amounts)
    const columns = _getColumnsFromAmounts(allAmounts)
    const columnsCount = columns.length || defaultColumnsCount

    setColumnsWidth(worksheet, columnsCount)

    let row = 1
    let rowWithValues = []
    let currentAmounts = statementsByPeriods[0].amounts
    let currentAgreementId = currentAmounts[0]?.coopGroup?.agreementId
    let currentColumns = columns
    let currentLayout = getColumnLayout(columnsCount)

    for (const statement of statementsByPeriods) {
      const statementAgreementId = statement.amounts[0]?.coopGroup?.agreementId
      if (statementAgreementId !== currentAgreementId) {
        row = _setTotalSupplier(
          worksheet,
          row,
          rowWithValues,
          currentAmounts,
          supplierName,
          totalAmountsCellsBySuppliers,
          currentColumns,
          currentLayout
        )
        rowWithValues = []
        currentAmounts = statement.amounts
        currentAgreementId = statementAgreementId
        const statementColumns = _getColumnsFromAmounts(currentAmounts)
        currentColumns = statementColumns.length > 0 ? statementColumns : columns
        currentLayout = getColumnLayout(currentColumns.length || defaultColumnsCount)
      }
      row = _createSupplierStatementsReport(worksheet, statement, row, rowWithValues, currentColumns, currentLayout)
    }
    const lastAmounts = statementsByPeriods[statementsByPeriods.length - 1].amounts
    const lastStatementColumns = _getColumnsFromAmounts(lastAmounts)
    const lastColumns = lastStatementColumns.length > 0 ? lastStatementColumns : columns
    const lastLayout = getColumnLayout(lastColumns.length || defaultColumnsCount)
    row = _setTotalSupplier(
      worksheet,
      row,
      rowWithValues,
      lastAmounts,
      supplierName,
      totalAmountsCellsBySuppliers,
      lastColumns,
      lastLayout
    )
  }

  _addTotalTab(workbook, totalAmountsCellsBySuppliers)

  const buffer = await workbook.xlsx.writeBuffer()
  FileSaver.saveAs(new Blob([buffer]), `Rapport_des_déclarations.xlsx`)
}
